import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentKind, AssistantMode, HealthInfo, OrgMember, Session, SessionSort, SessionSummary, StreamEvent, TagCount, ToolEvent, Turn, TurnUsage } from "../types";
import type { SourceFilter } from "../components/SearchFiltersPopover";
import { useInfinite, type Page } from "./useInfinite";
import { setBranding } from "../branding";

const SESSION_KEY = "educon-assistant-session-id";
const MODE_CONV_KEY = "educon-assistant-mode-conv";
const FILTERS_KEY = "educon-assistant-conv-filters";

/** Filtros da lista de conversas persistidos no navegador (voltam ao reentrar). */
interface PersistedFilters {
  tagFilter: string;
  assignedFilter: string;
  pinnedOnly: boolean;
  dateFrom: string;
  dateTo: string;
  sessionSort: SessionSort;
  sourceFilter: SourceFilter;
}
function readFilters(): Partial<PersistedFilters> {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    return raw ? (JSON.parse(raw) as Partial<PersistedFilters>) : {};
  } catch {
    return {};
  }
}

interface UseChatOptions {
  /** Modo padrão do usuário (persistido no servidor). */
  defaultMode: AssistantMode;
  /** Modelo padrão do usuário (persistido no servidor). */
  defaultModel: string;
  /** Persiste preferências no servidor. */
  onUpdateSettings: (patch: { defaultMode?: AssistantMode; defaultModel?: string }) => void;
}

function readConvModeMap(): Record<string, AssistantMode> {
  try {
    const raw = localStorage.getItem(MODE_CONV_KEY);
    return raw ? (JSON.parse(raw) as Record<string, AssistantMode>) : {};
  } catch {
    return {};
  }
}

function writeConvMode(sessionId: string, mode: AssistantMode | ""): void {
  const map = readConvModeMap();
  if (mode) map[sessionId] = mode;
  else delete map[sessionId];
  localStorage.setItem(MODE_CONV_KEY, JSON.stringify(map));
}

function newSessionId(): string {
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readSessionFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("s");
  } catch {
    return null;
  }
}

function writeSessionToUrl(id: string): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("s", id);
    window.history.pushState({ sessionId: id }, "", url);
  } catch {
    /* noop */
  }
}

export function useChat({ defaultMode, defaultModel, onUpdateSettings }: UseChatOptions) {
  const [sessionId, setSessionId] = useState<string>(() => {
    const fromUrl = readSessionFromUrl();
    if (fromUrl) {
      localStorage.setItem(SESSION_KEY, fromUrl);
      return fromUrl;
    }
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      writeSessionToUrl(stored);
      return stored;
    }
    const fresh = newSessionId();
    localStorage.setItem(SESSION_KEY, fresh);
    writeSessionToUrl(fresh);
    return fresh;
  });

  // popstate (back/forward do browser)
  useEffect(() => {
    const onPop = () => {
      const fromUrl = readSessionFromUrl();
      if (fromUrl && fromUrl !== sessionId) {
        setSessionId(fromUrl);
        localStorage.setItem(SESSION_KEY, fromUrl);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [sessionId]);

  const [turns, setTurns] = useState<Turn[]>([]);
  // espelho da contagem de turnos pra decisões fora do render (ex.: saber se
  // a mensagem enviada é a 1ª da conversa, pra inserir na sidebar na hora)
  const turnsCountRef = useRef(0);
  turnsCountRef.current = turns.length;
  const [shareId, setShareIdState] = useState<string | null>(null);
  const [sharedAt, setSharedAt] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [summarizedAt, setSummarizedAt] = useState<string | null>(null);
  const [privateNote, setPrivateNoteState] = useState<string>("");
  const [canManageSession, setCanManageSession] = useState(true);
  const [tools, setTools] = useState<ToolEvent[]>([]);
  // espelho pro merge das tools no turno ao finalizar (handler estável)
  const toolsRef = useRef<ToolEvent[]>([]);
  useEffect(() => {
    toolsRef.current = tools;
  }, [tools]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // filtros persistidos no navegador - voltam ao estado anterior ao reentrar
  const [initialFilters] = useState(readFilters);
  const [tagFilter, setTagFilter] = useState(initialFilters.tagFilter ?? "");
  const [availableTags, setAvailableTags] = useState<TagCount[]>([]);
  const [assignedFilter, setAssignedFilter] = useState(initialFilters.assignedFilter ?? "");
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [pinnedOnly, setPinnedOnly] = useState(initialFilters.pinnedOnly ?? false);
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(initialFilters.dateTo ?? "");
  const [sessionSort, setSessionSort] = useState<SessionSort>(initialFilters.sessionSort ?? "recent");
  // Por padrão mostra só as conversas manuais (painel); as de origem API ficam
  // ocultas até a pessoa escolher "API" ou "Todas" no filtro de Origem.
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(initialFilters.sourceFilter ?? "web");
  // persiste os filtros a cada mudança
  useEffect(() => {
    const data: PersistedFilters = { tagFilter, assignedFilter, pinnedOnly, dateFrom, dateTo, sessionSort, sourceFilter };
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(data));
    } catch {
      /* quota/privado - ignora */
    }
  }, [tagFilter, assignedFilter, pinnedOnly, dateFrom, dateTo, sessionSort, sourceFilter]);
  const [sessionsVersion, setSessionsVersion] = useState(0);
  const bumpSessions = useCallback(() => setSessionsVersion((v) => v + 1), []);
  const [isStreaming, setIsStreaming] = useState(false);
  const [status, setStatus] = useState<{ text: string; kind: "info" | "error" | "success" } | null>(null);
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [model, setModelState] = useState<string>(defaultModel);
  // motor (claude/codex) - não é preferência persistida, sempre nasce Claude
  // por padrão; troca vale só pra esta sessão/conversa.
  const [agent, setAgentState] = useState<AgentKind>("claude");
  const setAgent = useCallback((a: AgentKind) => {
    setAgentState(a);
    setModelState(""); // catálogo de modelo muda por motor - reseta pro default dele
  }, []);

  // Modo de resposta em três níveis: global (padrão do usuário) → conversa → mensagem.
  const [globalMode, setGlobalModeState] = useState<AssistantMode>(defaultMode);
  const [convMode, setConvModeState] = useState<AssistantMode | "">(
    () => readConvModeMap()[sessionId] ?? "",
  );
  const [msgMode, setMsgMode] = useState<AssistantMode | "">("");

  // modo efetivo: mensagem sobrepõe conversa, que sobrepõe o global.
  const effectiveMode: AssistantMode = msgMode || convMode || globalMode;

  const setGlobalMode = useCallback(
    (m: AssistantMode) => {
      setGlobalModeState(m);
      onUpdateSettings({ defaultMode: m });
    },
    [onUpdateSettings],
  );

  const setConvMode = useCallback(
    (m: AssistantMode | "") => {
      setConvModeState(m);
      writeConvMode(sessionId, m);
    },
    [sessionId],
  );

  const streamingTextRef = useRef<string>("");
  // Throttle do streaming: aplicar CADA token no estado re-renderiza (e
  // re-parseia o markdown) a conversa toda - resposta longa travava a tela.
  // Tokens acumulam no ref e o estado atualiza no máximo ~8x/segundo.
  const streamFlushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushStreamingText = useCallback(() => {
    if (streamFlushTimer.current) {
      clearTimeout(streamFlushTimer.current);
      streamFlushTimer.current = null;
    }
    setTurns((prev) => setLastAssistant(prev, (t) => ({ ...t, content: streamingTextRef.current })));
  }, []);
  const scheduleStreamFlush = useCallback(() => {
    if (streamFlushTimer.current) return; // já agendado nesta janela
    streamFlushTimer.current = setTimeout(() => {
      streamFlushTimer.current = null;
      setTurns((prev) => setLastAssistant(prev, (t) => ({ ...t, content: streamingTextRef.current })));
    }, 120);
  }, []);
  const lastUserMessageRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const stoppedRef = useRef<boolean>(false);
  // sessão atualmente visível (para guardar updates de stream ao trocar de chat)
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  // aborta apenas o LEITOR do stream (detach) — o job segue no servidor
  const streamAbortRef = useRef<AbortController | null>(null);

  const setModel = useCallback(
    (m: string) => {
      setModelState(m);
      onUpdateSettings({ defaultModel: m });
    },
    [onUpdateSettings],
  );

  // health
  useEffect(() => {
    fetch("/api/health")
      .then((r) => r.json())
      .then((j: HealthInfo) => {
        setHealth(j);
        setBranding(j);
      })
      .catch(() => setHealth(null));
  }, []);

  // Busca debounced
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 220);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Lista de conversas com scroll infinito (reseta ao buscar, filtrar por tag ou após mutações)
  const fetchSessionsPage = useCallback(
    async (offset: number): Promise<Page<SessionSummary>> => {
      const q = debouncedQuery ? `&q=${encodeURIComponent(debouncedQuery)}` : "";
      const tag = tagFilter ? `&tag=${encodeURIComponent(tagFilter)}` : "";
      const assigned = assignedFilter ? `&assignedTo=${encodeURIComponent(assignedFilter)}` : "";
      const pinned = pinnedOnly ? `&pinnedOnly=1` : "";
      const from = dateFrom ? `&from=${encodeURIComponent(dateFrom)}` : "";
      const to = dateTo ? `&to=${encodeURIComponent(dateTo)}` : "";
      const sort = sessionSort !== "recent" ? `&sort=${encodeURIComponent(sessionSort)}` : "";
      const source = sourceFilter ? `&source=${encodeURIComponent(sourceFilter)}` : "";
      const r = await fetch(`/api/sessions?offset=${offset}&limit=20${q}${tag}${assigned}${pinned}${from}${to}${sort}${source}`);
      if (!r.ok) return { items: [], hasMore: false };
      return (await r.json()) as Page<SessionSummary>;
    },
    [debouncedQuery, tagFilter, assignedFilter, pinnedOnly, dateFrom, dateTo, sessionSort, sourceFilter],
  );
  const sessionsInf = useInfinite<SessionSummary>(fetchSessionsPage, [
    debouncedQuery,
    tagFilter,
    assignedFilter,
    pinnedOnly,
    dateFrom,
    dateTo,
    sessionSort,
    sourceFilter,
    sessionsVersion,
  ]);
  const sessions = sessionsInf.items;

  // Tags em uso (pros chips de filtro) - recarrega junto com a lista de conversas.
  useEffect(() => {
    fetch("/api/sessions/tags")
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j: { items: TagCount[] }) => setAvailableTags(j.items ?? []))
      .catch(() => setAvailableTags([]));
  }, [sessionsVersion]);

  // Colegas da org ativa (pro seletor de "atribuir a").
  useEffect(() => {
    fetch("/api/org/members")
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j: { items: OrgMember[] }) => setOrgMembers(j.items ?? []))
      .catch(() => setOrgMembers([]));
  }, []);

  // ao trocar de conversa, recarrega o override de modo e zera o da mensagem
  useEffect(() => {
    setConvModeState(readConvModeMap()[sessionId] ?? "");
    setMsgMode("");
  }, [sessionId]);

  // Aplica um evento de stream à conversa VISÍVEL (o chamador garante o guard).
  const applyStreamEvent = useCallback((evt: StreamEvent) => {
    switch (evt.kind) {
      case "snapshot": {
        streamingTextRef.current = evt.text;
        const snapTools = evt.tools.map((t, i) => ({ id: `tool-snap-${i}`, name: t.name, input: t.input }));
        toolsRef.current = snapTools; // síncrono: o done pode chegar no mesmo lote
        setTools(snapTools);
        setTurns((prev) => setLastAssistant(prev, (t) => ({ ...t, content: evt.text })));
        break;
      }
      case "token":
        streamingTextRef.current += evt.text;
        scheduleStreamFlush();
        break;
      case "tool_use": {
        const item = { id: `tool-${toolsRef.current.length}-${evt.name}`, name: evt.name, input: evt.input };
        toolsRef.current = [...toolsRef.current, item]; // síncrono (ver snapshot)
        setTools((prev) => [...prev, item]);
        break;
      }
      case "tool_result":
        if (evt.isError) {
          setTools((prev) => {
            const u = [...prev];
            const last = u[u.length - 1];
            if (last) u[u.length - 1] = { ...last, isError: true };
            return u;
          });
        }
        break;
      case "done": {
        flushStreamingText();
        // anexa as ferramentas usadas AO TURNO (o backend persiste igual) -
        // sem isso o acordeão sumia da mensagem até recarregar o histórico
        const usedTools = toolsRef.current.map((t) => ({ name: t.name, input: t.input }));
        if (usedTools.length) {
          setTurns((prev) => setLastAssistant(prev, (t) => ({ ...t, tools: usedTools })));
        }
        const dur = evt.durationMs ? `${(evt.durationMs / 1000).toFixed(1)}s` : "?";
        const cost = evt.totalCostUsd != null ? `$${evt.totalCostUsd.toFixed(4)}` : "—";
        setTurns((prev) =>
          setLastAssistant(prev, (t) => ({
            ...t,
            usage: evt.usage,
            costUsd: evt.totalCostUsd,
            durationMs: evt.durationMs,
          })),
        );
        setStatus({ text: `Resposta pronta em ${dur} • ${cost}`, kind: "success" });
        setTimeout(() => setStatus(null), 5000);
        break;
      }
      case "aborted":
        setTurns((prev) =>
          setLastAssistant(prev, (t) => ({ ...t, content: streamingTextRef.current || t.content, aborted: true })),
        );
        setStatus({ text: "Resposta interrompida.", kind: "info" });
        setTimeout(() => setStatus(null), 4000);
        break;
      case "error":
        flushStreamingText();
        setTurns((prev) => setLastAssistant(prev, (t) => ({ ...t, errored: true, errorMessage: evt.message })));
        setStatus({ text: `Erro: ${evt.message}`, kind: "error" });
        break;
      case "start":
        break;
    }
  }, [flushStreamingText, scheduleStreamFlush]);

  // Consome um Response SSE; só mexe na UI enquanto a conversa é a visível.
  // Devolve se o stream terminou com evento FINAL (done/aborted/error) - sem
  // ele, a conexão caiu no meio (rede/reinício do serviço) e o chamador deve
  // tentar reanexar (recoverAfterDrop), nunca deixar a conversa muda.
  const consumeStream = useCallback(
    async (streamSessionId: string, resp: Response): Promise<boolean> => {
      if (!resp.body) return false;
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let pending = "";
      const viewing = () => sessionIdRef.current === streamSessionId;
      if (viewing()) setIsStreaming(true);
      let finished = false;
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          pending += decoder.decode(value, { stream: true });
          const events = pending.split("\n\n");
          pending = events.pop() ?? "";
          for (const evt of events) {
            if (!evt.trim()) continue;
            const parsed = parseSSE(evt);
            if (!parsed) continue;
            if (parsed.kind === "done" || parsed.kind === "aborted" || parsed.kind === "error") finished = true;
            if (viewing()) applyStreamEvent(parsed);
          }
        }
      } catch {
        // leitor abortado (detach) ou rede — o job segue no servidor
      } finally {
        if (viewing()) {
          setIsStreaming(false);
          if (finished) bumpSessions();
        }
      }
      return finished;
    },
    [applyStreamEvent, bumpSessions],
  );

  /**
   * A conexão do stream caiu SEM evento final (rede instável ou reinício do
   * serviço no deploy). Tenta reanexar ao job algumas vezes; se o job não
   * existe mais, recarrega o histórico e, se a resposta ficou pela metade
   * (último turno é do usuário), marca o turno com erro explícito - a pessoa
   * vê o que houve e tem o "Tentar novamente". A conversa NUNCA fica muda.
   */
  const recoverAfterDrop = useCallback(
    async (sid: string) => {
      for (let attempt = 0; attempt < 3; attempt++) {
        if (sessionIdRef.current !== sid || stoppedRef.current) return;
        await new Promise((ok) => setTimeout(ok, 1000 * (attempt + 1)));
        if (sessionIdRef.current !== sid || stoppedRef.current) return;
        try {
          const controller = new AbortController();
          const sr = await fetch(`/api/stream?sessionId=${encodeURIComponent(sid)}`, { signal: controller.signal });
          if (sessionIdRef.current !== sid) {
            controller.abort();
            return;
          }
          if (sr.ok && sr.status !== 204 && sr.body) {
            streamAbortRef.current = controller;
            const finished = await consumeStream(sid, sr);
            if (finished || controller.signal.aborted) return;
            continue; // caiu de novo - tenta mais uma vez
          }
          // 204/erro: o job não existe mais (terminou ou morreu) - o histórico decide
          const hr = await fetch(`/api/history?sessionId=${encodeURIComponent(sid)}`);
          if (!hr.ok) continue;
          if (sessionIdRef.current !== sid) return;
          const j = (await hr.json()) as Session | null;
          const turns = j?.turns ?? [];
          const last = turns[turns.length - 1];
          if (last && last.role === "user" && !last.deletedAt) {
            // a resposta morreu no meio (ex.: reinício do serviço) - feedback explícito
            setTurns([
              ...turns,
              {
                role: "assistant",
                content: streamingTextRef.current,
                ts: new Date().toISOString(),
                agent,
                errored: true,
                errorMessage: "A conexão caiu e a resposta não foi concluída. Use \"Tentar novamente\".",
              },
            ]);
            setStatus({ text: "A resposta foi interrompida - use \"Tentar novamente\".", kind: "error" });
          } else {
            // o job terminou enquanto estávamos desconectados: mostra o resultado
            setTurns(turns);
          }
          bumpSessions();
          return;
        } catch {
          /* rede ainda instável - próxima tentativa */
        }
      }
      if (sessionIdRef.current === sid) {
        setStatus({ text: "A conexão caiu durante a resposta. Recarregue a página pra ver o estado atual.", kind: "error" });
      }
    },
    [consumeStream, bumpSessions, agent],
  );

  // Ao trocar de conversa: detach do stream anterior (o job segue no servidor),
  // carrega o histórico e, se houver resposta em andamento, reanexa ao stream.
  useEffect(() => {
    const sid = sessionId;
    let cancelled = false;

    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    setIsStreaming(false);
    setTools([]);
    streamingTextRef.current = "";
    stoppedRef.current = false;
    setHistoryLoading(true);

    void (async () => {
      try {
        // histórico com RETRY: durante um deploy/instabilidade a 1ª tentativa
        // falha e a conversa aparecia TOTALMENTE em branco, sem feedback.
        let j: Session | null = null;
        let loaded = false;
        for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
          if (attempt > 0) await new Promise((ok) => setTimeout(ok, 1200 * attempt));
          if (cancelled) break;
          try {
            const r = await fetch(`/api/history?sessionId=${encodeURIComponent(sid)}`);
            if (r.ok) {
              j = (await r.json()) as Session;
              loaded = true;
              break;
            }
            if (r.status === 404 || r.status === 403) {
              loaded = true; // sessão nova/sem acesso - vazio mesmo, não é falha
              break;
            }
          } catch {
            /* rede - tenta de novo */
          }
        }
        if (!cancelled) {
          if (loaded) {
            setTurns(j?.turns ?? []);
            if (j?.model) setModelState(j.model);
            setShareIdState(j?.shareId ?? null);
            setSharedAt(j?.sharedAt ?? null);
            setSummary(j?.summary ?? null);
            setSummarizedAt(j?.summarizedAt ?? null);
            setPrivateNoteState(j?.privateNote ?? "");
            setCanManageSession(j?.canManage ?? true);
          } else {
            // NÃO apaga o que está na tela: avisa e deixa o caminho de recarga
            setStatus({ text: "Não consegui carregar a conversa agora - recarregue a página em instantes.", kind: "error" });
          }
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
      if (cancelled || sessionIdRef.current !== sid) return;

      // reanexa se houver job em andamento nesta conversa
      try {
        const controller = new AbortController();
        const sr = await fetch(`/api/stream?sessionId=${encodeURIComponent(sid)}`, {
          signal: controller.signal,
        });
        if (cancelled || sessionIdRef.current !== sid) {
          controller.abort();
          return;
        }
        if (sr.status === 204 || !sr.ok || !sr.body) {
          // pode ter finalizado entre o history e o stream — recarrega
          const r2 = await fetch(`/api/history?sessionId=${encodeURIComponent(sid)}`);
          if (!cancelled && r2.ok && sessionIdRef.current === sid) {
            const j2 = (await r2.json()) as Session;
            setTurns(j2.turns ?? []);
          }
          return;
        }
        streamAbortRef.current = controller;
        streamingTextRef.current = "";
        // turno assistant vazio que o snapshot/tokens vão preencher
        setTurns((prev) => [...prev, { role: "assistant", content: "", ts: new Date().toISOString(), agent }]);
        const finished = await consumeStream(sid, sr);
        if (!finished && !controller.signal.aborted && !cancelled && sessionIdRef.current === sid) {
          await recoverAfterDrop(sid);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, consumeStream, recoverAfterDrop]);

  const startNewSession = useCallback(() => {
    const fresh = newSessionId();
    localStorage.setItem(SESSION_KEY, fresh);
    writeSessionToUrl(fresh);
    setSessionId(fresh);
    setTurns([]);
    setTools([]);
    setStatus(null);
    setShareIdState(null);
    setSharedAt(null);
    setSummary(null);
    setSummarizedAt(null);
    setPrivateNoteState("");
    setCanManageSession(true);
  }, []);

  const switchSession = useCallback((id: string) => {
    localStorage.setItem(SESSION_KEY, id);
    writeSessionToUrl(id);
    setSessionId(id);
    setStatus(null);
  }, []);

  const deleteSession = useCallback(
    async (id: string) => {
      try {
        await fetch(`/api/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
        if (id === sessionId) {
          startNewSession();
        }
        bumpSessions();
      } catch (err) {
        console.error("Erro ao deletar sessão", err);
      }
    },
    [sessionId, startNewSession, bumpSessions],
  );

  /** Soft delete de UMA mensagem (turn) da conversa atual - some da visão (otimista). */
  const deleteTurn = useCallback(
    async (idx: number) => {
      const sid = sessionIdRef.current;
      setTurns((prev) => prev.map((t, i) => (i === idx ? { ...t, deletedAt: new Date().toISOString() } : t)));
      try {
        await fetch(`/api/sessions/${encodeURIComponent(sid)}/turns/${idx}`, { method: "DELETE" });
      } catch (err) {
        console.error("Erro ao apagar mensagem", err);
      }
    },
    [],
  );

  /** Gera (ou reaproveita) o link público read-only da conversa atual. */
  const shareSession = useCallback(async (): Promise<string | null> => {
    try {
      const r = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/share`, { method: "POST" });
      if (!r.ok) return null;
      const j = (await r.json()) as { shareId: string; sharedAt: string; url: string };
      setShareIdState(j.shareId);
      setSharedAt(j.sharedAt);
      return j.url;
    } catch {
      return null;
    }
  }, [sessionId]);

  /** Revoga o link público da conversa atual. */
  const unshareSession = useCallback(async () => {
    try {
      await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/share`, { method: "DELETE" });
    } catch {
      /* ignore */
    }
    setShareIdState(null);
    setSharedAt(null);
  }, [sessionId]);

  /** Gera (ou reaproveita, se já existir e `force` não for passado) o resumo IA da conversa atual. */
  const summarizeSession = useCallback(
    async (opts?: { force?: boolean }): Promise<{ summary?: string; error?: string }> => {
      try {
        const r = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/summarize`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ force: Boolean(opts?.force) }),
        });
        const j = (await r.json()) as { summary?: string; summarizedAt?: string; error?: string };
        if (!r.ok) return { error: j.error ?? "falha ao gerar o resumo" };
        setSummary(j.summary ?? null);
        setSummarizedAt(j.summarizedAt ?? null);
        return { summary: j.summary };
      } catch {
        return { error: "falha ao gerar o resumo" };
      }
    },
    [sessionId],
  );

  /** Salva a nota privada da conversa atual (só visível pra quem gerencia a conversa). */
  const updatePrivateNote = useCallback(
    async (note: string): Promise<{ error?: string }> => {
      try {
        const r = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ privateNote: note }),
        });
        const j = (await r.json().catch(() => ({}))) as { error?: string; privateNote?: string };
        if (!r.ok) return { error: j.error ?? "falha ao salvar a nota" };
        setPrivateNoteState(j.privateNote ?? note);
        return {};
      } catch {
        return { error: "falha ao salvar a nota" };
      }
    },
    [sessionId],
  );

  const ask = useCallback(
    async (message: string, opts?: { isRetry?: boolean }) => {
      if (isStreaming) return;
      lastUserMessageRef.current = message;
      const sid = sessionId;
      // 1ª mensagem da conversa? (antes de empurrar os turnos otimistas) - se
      // for, a conversa ainda não está na sidebar e precisa entrar na hora
      const isFirstMessage = !opts?.isRetry && turnsCountRef.current === 0;

      if (opts?.isRetry) {
        setTurns((prev) => {
          const trimmed = [...prev];
          const last = trimmed[trimmed.length - 1];
          if (last && last.role === "assistant") trimmed.pop();
          return trimmed;
        });
      } else {
        setTurns((prev) => [...prev, { role: "user", content: message, ts: new Date().toISOString() }]);
      }
      setTurns((prev) => [...prev, { role: "assistant", content: "", ts: new Date().toISOString(), agent }]);
      streamingTextRef.current = "";
      setTools([]);
      setStatus(null);
      stoppedRef.current = false;

      const sendMode: AssistantMode = msgMode || convMode || globalMode;
      if (msgMode) setMsgMode("");

      const controller = new AbortController();
      streamAbortRef.current = controller;
      try {
        const resp = await fetch("/api/ask", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId: sid, message, model: model || undefined, mode: sendMode, agent }),
          signal: controller.signal,
        });
        if (resp.status === 409) {
          setStatus({ text: "Já há uma resposta em andamento nesta conversa.", kind: "info" });
          const hr = await fetch(`/api/history?sessionId=${encodeURIComponent(sid)}`);
          if (hr.ok && sessionIdRef.current === sid) {
            const j: Session = await hr.json();
            setTurns(j.turns ?? []);
          }
          return;
        }
        if (!resp.ok) {
          // erro de negócio (402 orçamento/pagamento, 429 rate limit...): mostra a
          // MENSAGEM do servidor, não "HTTP 402" seco
          const j = (await resp.json().catch(() => ({}))) as { error?: string };
          throw new Error(j.error ?? `HTTP ${resp.status}`);
        }
        if (!resp.body) throw new Error(`HTTP ${resp.status}`);
        // O servidor já persistiu a conversa + o turno do usuário antes de
        // responder. Na 1ª mensagem, atualiza a sidebar AGORA (antes dependia
        // do fim da resposta - a conversa só aparecia depois, ou após reload).
        if (isFirstMessage) bumpSessions();
        const finished = await consumeStream(sid, resp);
        if (!finished && !controller.signal.aborted && sessionIdRef.current === sid) {
          // a conexão caiu no meio da resposta - reanexa ou dá feedback explícito
          await recoverAfterDrop(sid);
        }
      } catch (err) {
        if (controller.signal.aborted) return; // detach: o job continua no servidor
        const msg = err instanceof Error ? err.message : String(err);
        if (sessionIdRef.current === sid) {
          setTurns((prev) => setLastAssistant(prev, (t) => ({ ...t, errored: true, errorMessage: msg })));
          setStatus({ text: `Erro: ${msg}`, kind: "error" });
          setIsStreaming(false);
        }
      }
    },
    [isStreaming, sessionId, model, agent, msgMode, convMode, globalMode, consumeStream, recoverAfterDrop, bumpSessions],
  );

  const retry = useCallback(() => {
    if (isStreaming) return;
    // A ref só existe se o ask rodou NESTA aba - depois de recarregar (erro
    // persistido no histórico) ela está vazia e o botão virava no-op. Fallback:
    // a última mensagem de usuário da própria conversa.
    let last = lastUserMessageRef.current;
    if (!last) {
      for (let i = turns.length - 1; i >= 0; i--) {
        const t = turns[i]!;
        if (t.role === "user" && !t.deletedAt && t.content.trim()) {
          last = t.content;
          break;
        }
      }
    }
    if (!last) return;
    void ask(last, { isRetry: true });
  }, [ask, isStreaming, turns]);

  /** Interrompe (aborta) o job em andamento no servidor. */
  const stop = useCallback(async () => {
    if (!isStreaming) return;
    stoppedRef.current = true;
    try {
      await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/stop`, { method: "POST" });
    } catch {
      /* ignore — o servidor emitirá "aborted" quando parar */
    }
  }, [isStreaming, sessionId]);

  /** Renomeia a conversa (título custom). title vazio volta ao derivado. */
  const renameSession = useCallback(
    async (id: string, title: string) => {
      try {
        await fetch(`/api/sessions/${encodeURIComponent(id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title }),
        });
        bumpSessions();
      } catch (err) {
        console.error("Erro ao renomear sessão", err);
      }
    },
    [bumpSessions],
  );

  /** Troca as tags (categorias livres) de uma conversa. */
  const updateTags = useCallback(
    async (id: string, tags: string[]) => {
      // otimista
      sessionsInf.setItems((prev) => prev.map((s) => (s.id === id ? { ...s, tags } : s)));
      try {
        await fetch(`/api/sessions/${encodeURIComponent(id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tags }),
        });
      } catch (err) {
        console.error("Erro ao atualizar tags", err);
      } finally {
        bumpSessions();
      }
    },
    [bumpSessions, sessionsInf],
  );

  /** Exclui uma tag de TODAS as conversas do próprio usuário (chip some da sidebar). */
  const deleteTag = useCallback(
    async (tag: string) => {
      try {
        const r = await fetch(`/api/sessions/tags/${encodeURIComponent(tag)}`, { method: "DELETE" });
        if (!r.ok) {
          console.error("Erro ao excluir tag", await r.text().catch(() => r.status));
          return;
        }
        // tag excluída era o filtro ativo: limpa senão a lista fica vazia
        setTagFilter((cur) => (cur === tag ? "" : cur));
      } catch (err) {
        console.error("Erro ao excluir tag", err);
      } finally {
        bumpSessions(); // recarrega a lista e os chips (mesmo gatilho)
      }
    },
    [bumpSessions],
  );

  /** Atribui (ou remove a atribuição de) uma conversa a um colega da org. */
  const assignSession = useCallback(
    async (id: string, userId: string | null) => {
      const member = userId ? orgMembers.find((m) => m.id === userId) : undefined;
      // otimista
      sessionsInf.setItems((prev) =>
        prev.map((s) => (s.id === id ? { ...s, assignedTo: userId, assignedToEmail: member?.email ?? null } : s)),
      );
      try {
        await fetch(`/api/sessions/${encodeURIComponent(id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignedTo: userId }),
        });
      } catch (err) {
        console.error("Erro ao atribuir conversa", err);
      } finally {
        bumpSessions();
      }
    },
    [bumpSessions, sessionsInf, orgMembers],
  );

  /** Fixa/desafixa a conversa no topo da sidebar. */
  const togglePin = useCallback(
    async (id: string, pinned: boolean) => {
      // otimista
      sessionsInf.setItems((prev) => prev.map((s) => (s.id === id ? { ...s, pinned } : s)));
      try {
        await fetch(`/api/sessions/${encodeURIComponent(id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pinned }),
        });
      } catch (err) {
        console.error("Erro ao fixar sessão", err);
      } finally {
        bumpSessions();
      }
    },
    [bumpSessions, sessionsInf],
  );

  /**
   * Regenera a última resposta como NOVA versão (sem duplicar a pergunta).
   * Streama via /api/sessions/:id/regenerate.
   */
  const regenerate = useCallback(async () => {
    if (isStreaming) return;
    setIsStreaming(true);
    setStatus(null);
    streamingTextRef.current = "";
    setTools([]);
    stoppedRef.current = false;
    const controller = new AbortController();
    abortRef.current = controller;

    // marca o último assistant como "pensando" — limpa content até stream começar
    setTurns((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last && last.role === "assistant") {
        updated[updated.length - 1] = {
          ...last,
          content: "",
          errored: false,
          errorMessage: undefined,
        };
      }
      return updated;
    });

    try {
      const resp = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/regenerate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: convMode || globalMode }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let pending = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        pending += decoder.decode(value, { stream: true });
        const events = pending.split("\n\n");
        pending = events.pop() ?? "";
        for (const evt of events) {
          if (!evt.trim()) continue;
          const parsed = parseSSE(evt);
          if (!parsed) continue;
          switch (parsed.kind) {
            case "token":
              streamingTextRef.current += parsed.text;
              setTurns((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = { ...last, content: streamingTextRef.current };
                }
                return updated;
              });
              break;
            case "tool_use":
              setTools((prev) => [
                ...prev,
                { id: `tool-${Date.now()}-${Math.random()}`, name: parsed.name, input: parsed.input },
              ]);
              break;
            case "done":
              setTurns((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    usage: parsed.usage as TurnUsage | undefined,
                    costUsd: parsed.totalCostUsd,
                    durationMs: parsed.durationMs,
                  };
                }
                return updated;
              });
              break;
            case "aborted":
              stoppedRef.current = true;
              break;
            case "error":
              setStatus({ text: `Erro: ${parsed.message}`, kind: "error" });
              break;
          }
        }
      }

      if (stoppedRef.current) {
        setStatus({ text: "Resposta interrompida.", kind: "info" });
        setTimeout(() => setStatus(null), 4000);
      }

      // recarrega histórico (pra trazer `versions` atualizado do backend)
      const r = await fetch(`/api/history?sessionId=${encodeURIComponent(sessionId)}`);
      const j: Session = await r.json();
      setTurns(j.turns ?? []);
      bumpSessions();
    } catch (err) {
      if (stoppedRef.current || (err instanceof DOMException && err.name === "AbortError")) {
        setStatus({ text: "Resposta interrompida.", kind: "info" });
        setTimeout(() => setStatus(null), 4000);
        // recarrega o parcial salvo pelo backend
        try {
          const r = await fetch(`/api/history?sessionId=${encodeURIComponent(sessionId)}`);
          const j: Session = await r.json();
          setTurns(j.turns ?? []);
        } catch {
          /* mantém estado local */
        }
        bumpSessions();
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus({ text: `Erro: ${msg}`, kind: "error" });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [isStreaming, sessionId, bumpSessions, convMode, globalMode]);

  /**
   * Marca like/dislike num turn específico (toggle se já estiver setado).
   */
  const setFeedback = useCallback(
    async (turnIdx: number, feedback: "like" | "dislike") => {
      const current = turns[turnIdx]?.feedback;
      const next = current === feedback ? null : feedback;
      // otimista
      setTurns((prev) => {
        const updated = [...prev];
        const t = updated[turnIdx];
        if (t) updated[turnIdx] = { ...t, feedback: next ?? undefined };
        return updated;
      });
      try {
        await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/turns/${turnIdx}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ feedback: next }),
        });
      } catch {
        // reverte se falhar
        setTurns((prev) => {
          const updated = [...prev];
          const t = updated[turnIdx];
          if (t) updated[turnIdx] = { ...t, feedback: current };
          return updated;
        });
      }
    },
    [sessionId, turns],
  );

  /**
   * Troca a versão visível de um turn.
   */
  const switchVersion = useCallback(
    async (turnIdx: number, versionIdx: number) => {
      const turn = turns[turnIdx];
      if (!turn || !turn.versions || versionIdx < 0 || versionIdx >= turn.versions.length) return;
      const v = turn.versions[versionIdx];
      if (!v) return;
      setTurns((prev) => {
        const updated = [...prev];
        const t = updated[turnIdx];
        if (t) {
          updated[turnIdx] = {
            ...t,
            versionIdx,
            content: v.content,
            usage: v.usage,
            costUsd: v.costUsd,
            durationMs: v.durationMs,
          };
        }
        return updated;
      });
      try {
        await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/turns/${turnIdx}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ versionIdx }),
        });
      } catch {
        /* mantém otimista */
      }
    },
    [sessionId, turns],
  );

  return {
    sessionId,
    turns,
    refreshSessions: bumpSessions,
    shareId,
    sharedAt,
    shareSession,
    unshareSession,
    summary,
    summarizedAt,
    summarizeSession,
    privateNote,
    canManageSession,
    updatePrivateNote,
    tools,
    historyLoading,
    sessions,
    sessionsLoading: sessionsInf.initialLoading,
    sessionsLoadingMore: sessionsInf.loadingMore,
    sessionsHasMore: sessionsInf.hasMore,
    sessionsSentinelRef: sessionsInf.sentinelRef,
    searchQuery,
    setSearchQuery,
    tagFilter,
    setTagFilter,
    availableTags,
    updateTags,
    deleteTag,
    assignedFilter,
    setAssignedFilter,
    orgMembers,
    assignSession,
    pinnedOnly,
    setPinnedOnly,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sessionSort,
    setSessionSort,
    sourceFilter,
    setSourceFilter,
    isStreaming,
    status,
    health,
    model,
    setModel,
    agent,
    setAgent,
    globalMode,
    setGlobalMode,
    convMode,
    setConvMode,
    msgMode,
    setMsgMode,
    effectiveMode,
    ask,
    retry,
    stop,
    regenerate,
    deleteTurn,
    setFeedback,
    switchVersion,
    startNewSession,
    switchSession,
    deleteSession,
    renameSession,
    togglePin,
  };
}

function parseSSE(raw: string): StreamEvent | null {
  const lines = raw.split("\n");
  let eventName = "message";
  let dataStr = "";
  for (const line of lines) {
    if (line.startsWith("event: ")) eventName = line.slice(7);
    else if (line.startsWith("data: ")) dataStr += line.slice(6);
  }
  if (!dataStr) return null;
  try {
    const data = JSON.parse(dataStr);
    switch (eventName) {
      case "start":
        return { kind: "start", sessionId: data.sessionId };
      case "snapshot":
        return {
          kind: "snapshot",
          sessionId: data.sessionId,
          text: typeof data.text === "string" ? data.text : "",
          tools: Array.isArray(data.tools) ? data.tools : [],
        };
      case "token":
        return { kind: "token", text: data.text };
      case "tool_use":
        return { kind: "tool_use", name: data.name, input: data.input };
      case "tool_result":
        return { kind: "tool_result", isError: data.is_error ?? false };
      case "done":
        return {
          kind: "done",
          durationMs: data.duration_ms,
          totalCostUsd: data.total_cost_usd,
          usage: data.usage,
        };
      case "aborted":
        return { kind: "aborted", partial: Boolean(data.partial) };
      case "error":
        return { kind: "error", message: data.message };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/** Atualiza o último turno se for do assistente (imutável). */
function setLastAssistant(prev: Turn[], fn: (t: Turn) => Turn): Turn[] {
  const updated = [...prev];
  const last = updated[updated.length - 1];
  if (last && last.role === "assistant") updated[updated.length - 1] = fn(last);
  return updated;
}
